import {
  formatNumber,
  formatNumberWithCurrency,
  getCurrency,
} from '@qred/shared-component-library/src/currency';
import { parseMarket } from './market.helper';
import { CountryCode } from '@qred/shared-enums';
import {
  companyNumberRegex,
  personalNumberRegex,
  additionalPersonalNumberRegex,
} from './validations.constants';

export const capitalizeFirstLetter = (s: string): string => {
  if (!s) return '';
  const firstLetter = s.charAt(0).toUpperCase();
  const rest = s.substring(1).toLocaleLowerCase();
  return firstLetter + rest;
};

export const formatAmount = (amount: number): string => {
  // TODO: market is now read from Auth0 token, what to do for public pages?
  // Default value is given so that public pages don't crash in the meanwhile.
  const market = window.__QRED_MARKET__ || 'se';
  return formatNumberWithCurrency(market, amount);
};

export const formatAmountWithoutCurrency = (
  amount: number | string
): string => {
  // TODO: market is now read from Auth0 token, what to do for public pages?
  // Default value is given so that public pages don't crash in the meanwhile.

  const market = window.__QRED_MARKET__ || 'se';
  return amount ? formatNumber(market, amount) : '';
};

export const removeNonDigits = (value: string): string =>
  value.replaceAll(/\D/g, '');

export const formatAmountNumbersOnly = (amount: string): string =>
  formatAmountWithoutCurrency(Number(removeNonDigits(amount)));

export const removeWhitespace = (value: string): string =>
  value.replaceAll(/\s/g, '');

export const formatValueNumbersOnly = (value: string): string => {
  let val = value;
  if (val.startsWith('0') && val.length > 1) {
    val = val.replace('0', '');
  }
  return removeNonDigits(val);
};

export const getMarketCurrency = (): { currency: string; unit: string } => {
  const market = window.__QRED_MARKET__ || 'se';
  return getCurrency(market);
};

const normalizeCompanyNumber = (
  companyNumber: string,
  countryCode: CountryCode
): string => {
  switch (countryCode) {
    case CountryCode.FI: {
      // Inserts '-' after the first 7 digits to produce the FI format: XXXXXXX-X
      return companyNumber.replace(/^(\d{7})/, '$1-');
    }
    case CountryCode.DE: {
      // No DE-specific formatting; trim only.
      return companyNumber;
    }
    case CountryCode.SE:
    case CountryCode.BE:
    case CountryCode.NO:
    case CountryCode.DK:
    case CountryCode.NL: {
      // Removes hyphens, whitespace, commas, and dots
      return companyNumber.replaceAll(/[-\s,.]/g, '');
    }
    default:
      return companyNumber;
  }
};

// Normalizes company number to the backend format.
// Wrong format causes application rejection.
export const normalizeCompanyNumberForBackend = (
  companyNumber: string | undefined,
  countryCode: CountryCode
) => {
  if (!companyNumber) {
    return companyNumber ?? '';
  }

  const trimmedCompanyNumber = companyNumber.trim();

  const isValidFormatForBackend = companyNumberRegex[countryCode].test(
    trimmedCompanyNumber
  );

  // Already normalized — return as-is.
  if (isValidFormatForBackend) {
    return trimmedCompanyNumber;
  }

  return normalizeCompanyNumber(trimmedCompanyNumber, countryCode);
};

const normalizePersonalNumber = (
  personalNumber: string,
  countryCode: CountryCode
): string => {
  switch (countryCode) {
    case CountryCode.SE: {
      // Removes '-' to produce the SE format: 12 consecutive digits (e.g. 19901231XXXX)
      return personalNumber.replace('-', '');
    }
    case CountryCode.FI: {
      // Uppercases any letters (e.g. century marker 'a' → 'A', check character 'y' → 'Y')
      return personalNumber.toUpperCase();
    }
    case CountryCode.BE: {
      // Normalizes to the BE format: DD.MM.YY-XXX.CC
      return personalNumber.replace(
        /(\d{2})(\d{2})(\d{2})(\d{3})(\d{2})/,
        '$1.$2.$3-$4.$5'
      );
    }
    case CountryCode.DK: {
      // Inserts '-' after the first 6 digits to produce the DK format: DDMMYY-XXXX
      return personalNumber.replace(/(\d{6})/, '$1-');
    }
    case CountryCode.NO:
    case CountryCode.NL:
    case CountryCode.DE:
      return personalNumber;
    default:
      return personalNumber;
  }
};

// Normalizes personal number to the backend format.
// Wrong format causes application rejection.
export const normalizePersonalNumberForBackend = (
  personalNumber: string | undefined,
  countryCode: CountryCode
) => {
  if (!personalNumber) {
    return personalNumber ?? '';
  }

  const trimmedPersonalNumber = personalNumber.trim();

  const isValidFormatForBackend = personalNumberRegex[countryCode].test(
    trimmedPersonalNumber
  );

  // Already normalized — return as-is.
  if (isValidFormatForBackend) {
    return trimmedPersonalNumber;
  }

  const isValidFormatForFrontend = additionalPersonalNumberRegex[
    countryCode
  ].test(trimmedPersonalNumber);

  // Recognized frontend variation — normalize to backend format.
  if (isValidFormatForFrontend) {
    return normalizePersonalNumber(trimmedPersonalNumber, countryCode);
  }

  // Unrecognized format — return as-is
  return trimmedPersonalNumber;
};

export const formatPersonalNumber = (
  personalNumber: string,
  market: CountryCode
) => {
  if (!personalNumber || !market) return personalNumber;

  switch (market.toUpperCase()) {
    case CountryCode.BE:
      return personalNumber.replace(
        /(\d{2})(\d{2})(\d{2})(\d{3})(\d{2})/,
        '$1.$2.$3-$4.$5'
      );

    case CountryCode.DK:
      // Sometimes the PN is already formatted, like when loggin into the internal-prod environment.
      // Format it only if it is not already formatted.

      if (personalNumber.indexOf('-') > 0) {
        return personalNumber;
      }
      return personalNumber.replace(/(\d{6})/, '$1-');

    default:
      return personalNumber;
  }
};

export const removeSpecialCharacters = (str: string) =>
  str.replaceAll(/[^a-zA-Z0-9]/g, '');

export const formatBankAccountNumber = (
  accountNumber: string | null,
  market: CountryCode
) => {
  if (!accountNumber || !market) return '';
  switch (market.toUpperCase()) {
    case CountryCode.FI:
      return `FI${removeNonDigits(accountNumber)}`;
    case CountryCode.SE:
      return removeNonDigits(accountNumber);
    default:
      return removeWhitespace(accountNumber);
  }
};

export const formatZipCodeNL = (zipCode: string | undefined) => {
  if (!zipCode) return '';

  return zipCode
    .replaceAll(/[^a-zA-Z0-9]/g, '')
    .replaceAll(/(.{4})/g, '$1 ')
    .trim()
    .toUpperCase();
};

export const formatSize = (bytes: number, decimals = 1) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1000;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
};

export const formatOrganizationNumber = (organizationNumber: string) => {
  const market = parseMarket(window.__QRED_MARKET__) || CountryCode.SE;
  if (!organizationNumber) return '';

  if ([CountryCode.BE].includes(market)) {
    return organizationNumber.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1.$2.$3');
  }
  return organizationNumber;
};
